import { useCallback, useEffect, useRef, useState } from 'react';

type SpeakOptions = {
  lang?: string;
  rate?: number;
};

const hasSpeechSynthesis = () =>
  typeof window !== 'undefined' &&
  'speechSynthesis' in window &&
  'SpeechSynthesisUtterance' in window;

export function useSpeechSynthesis({ lang = 'en-US', rate = 0.9 }: SpeakOptions = {}) {
  const [isSupported, setIsSupported] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const hasAudio = () => typeof window !== 'undefined' && 'Audio' in window;

  useEffect(() => {
    setIsSupported(hasSpeechSynthesis() || hasAudio());

    return () => {
      if (hasSpeechSynthesis()) {
        window.speechSynthesis.cancel();
      }
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  const speakSpeech = useCallback((phrase: string) => {
    if (!hasSpeechSynthesis()) {
      setIsSpeaking(false);
      return false;
    }

    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(phrase);
    utterance.lang = lang;
    utterance.rate = rate;
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    utteranceRef.current = utterance;
    setIsSpeaking(true);
    window.speechSynthesis.speak(utterance);
    return true;
  }, [lang, rate]);

  const speak = useCallback((text: string, sound?: string | null) => {
    const phrase = text.trim();
    if (!phrase || (!hasSpeechSynthesis() && !hasAudio())) {
      setIsSpeaking(false);
      return false;
    }

    if (hasSpeechSynthesis()) {
      window.speechSynthesis.cancel();
    }
    audioRef.current?.pause();
    audioRef.current = null;

    const audioUrl = sound?.trim();
    if (audioUrl && hasAudio()) {
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      audio.onended = () => {
        if (audioRef.current === audio) {
          audioRef.current = null;
          setIsSpeaking(false);
        }
      };
      audio.onerror = () => {
        if (audioRef.current === audio) {
          audioRef.current = null;
          speakSpeech(phrase);
        }
      };
      setIsSpeaking(true);
      void audio.play().catch(() => {
        if (audioRef.current === audio) {
          audioRef.current = null;
          speakSpeech(phrase);
        }
      });
      return true;
    }

    return speakSpeech(phrase);
  }, [speakSpeech]);

  const cancel = useCallback(() => {
    if (hasSpeechSynthesis()) {
      window.speechSynthesis.cancel();
    }
    audioRef.current?.pause();
    audioRef.current = null;
    setIsSpeaking(false);
  }, []);

  return {
    cancel,
    isSpeaking,
    isSupported,
    speak,
  };
}
